import React, {useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {Button} from "src/components";
import {RouteWithIdAndFlowchartId} from "src/routes/types";
import DiscardModal from "../../../components/Modal/DiscardModal/DiscardModal";
import {useAppDispatch} from "../../../store/hooks";
import {resetVisitedNavItems, setActiveNavItem, setSaveClicked} from "../../slices/NavigationSlice";
import "./_form-header.scss";
import {EstateItem} from "src/EstateFlowchart/model/EstateFlowchartTypes";
import {EstateItemType} from "../../forms/EstateItemType";
import DeleteButton from "./DeleteButton";
import useProfileEditableState from "../../../hooks/useProfileEditableState";

type FormHeaderProps = {
    title: string,
    subheader?: string,
    estateItem?: EstateItem,
    estateItemType?: EstateItemType
    formHasBeenEdited: boolean,
    showSaveButton?: boolean,
    onSaveButtonClick?: () => void,
}

/**
 * FormHeader component renders a header section for a form.
 * It includes a title, an optional subheader, and buttons for cancel and save actions.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The main title to display in the header.
 * @param {string} [props.subheader] - An optional subheader to display above the title.
 * @param {boolean} props.formHasBeenEdited - Indicates if the form has been edited, triggering a modal on cancel.
 * @param {boolean} [props.showSaveButton] - Determines if the save button should be displayed.
 * @param {Function} [props.onSaveButtonClick] - Callback function for the save button click event.
 *
 * @returns {JSX.Element} The rendered FormHeader component.
 */
export default function FormHeader({
                                       title,
                                       subheader,
                                       estateItem,
                                       estateItemType,
                                       formHasBeenEdited,
                                       showSaveButton,
                                       onSaveButtonClick
                                   }: Readonly<FormHeaderProps>): JSX.Element {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const {id} = useParams<RouteWithIdAndFlowchartId>();
    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);


    const exitForm = () => {
        dispatch(setActiveNavItem(undefined));
        dispatch(setSaveClicked(false));
        dispatch(resetVisitedNavItems());
        history.push(`/Profile/${id}/ClientProfile/EstateFlowchart`);
    }

    const {isProfileWithProposalsOrArchived} = useProfileEditableState();

    const onClickCancel = () => {
        if (isProfileWithProposalsOrArchived) {
            exitForm();
        } else {
            formHasBeenEdited ? setIsDiscardModalOpen(true) : exitForm();
        }
    }

    return (
        <div className="form-header" data-testid="form-header" id="form-header">
            <div className="form-header-title">
                {subheader ? <span>{subheader}</span> : <></>}
                <h3 className={subheader ? "no-margin-top" : undefined}>{title}</h3>
            </div>
            <div className="form-header-button">
                {estateItemType && (
                    <DeleteButton
                        estateItemType={estateItemType}
                        estateItem={estateItem}
                    />
                )}
                <Button className="marginright-sm"
                        disabled={false}
                        icon="none"
                        kind="secondary"
                        onClick={onClickCancel}
                        size={"medium"}
                        type="button">
                    Cancel
                </Button>
                {showSaveButton &&
                    <Button className="marginleft-md"
                            icon="none"
                            kind="primary"
                            onClick={onSaveButtonClick}
                            size={"medium"}
                            disabled={isProfileWithProposalsOrArchived}
                            type="button">
                        Save
                    </Button>
                }
            </div>
            <DiscardModal isOpen={isDiscardModalOpen}
                          onClickKeepEditing={() => setIsDiscardModalOpen(false)}
                          onClickDiscardChanges={() => exitForm()}
                          isEditing={true}/>

        </div>
    )

}